/**
 * PeerReviewDialog — Form dialog for marking/updating peer review on a study.
 */
import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  Box,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../api/rpc';
import type { Study } from '../../api/types';

interface PeerReviewDialogProps {
  open: boolean;
  study: Study | null;
  onClose: () => void;
  onComplete?: () => void;
}

const REVIEW_VALUES = [
  { value: 1, label: '1 — Agree' },
  { value: 2, label: '2 — Minor' },
  { value: 3, label: '3 — Significant' },
  { value: 4, label: '4 — Miss' },
];

const ASSIGN_TYPES = [
  { value: '', label: '(none)' },
  { value: 'RANDOM', label: 'Random' },
  { value: 'SPECIFIC', label: 'Specific' },
];

export const PeerReviewDialog = ({
  open,
  study,
  onClose,
  onComplete,
}: PeerReviewDialogProps) => {
  const [assignType, setAssignType] = useState('');
  const [reviewValue, setReviewValue] = useState<number | ''>('');
  const [reviewNote, setReviewNote] = useState('');
  const [actionNeeded, setActionNeeded] = useState(false);
  const [isFollowUp, setIsFollowUp] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!study) return;
      await api.markPeerReview({
        stuKey: study.objKey,
        assignType: assignType || undefined,
        reviewValue: reviewValue !== '' ? reviewValue : undefined,
        reviewNote: reviewNote || undefined,
        actionNeeded: actionNeeded || undefined,
        isFollowUp: isFollowUp || undefined,
      });
    },
    onSuccess: () => {
      setAssignType('');
      setReviewValue('');
      setReviewNote('');
      setActionNeeded(false);
      setIsFollowUp(false);
      setError(null);
      onComplete?.();
      onClose();
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  if (!study) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Peer Review — {study.name}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            select
            fullWidth
            label="Assign Type"
            value={assignType}
            onChange={(e) => setAssignType(e.target.value)}
            size="small"
          >
            {ASSIGN_TYPES.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            label="Review Score"
            value={reviewValue}
            onChange={(e) => setReviewValue(e.target.value ? Number(e.target.value) : '')}
            size="small"
          >
            <MenuItem value="">
              (none)
            </MenuItem>
            {REVIEW_VALUES.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Review Note"
            value={reviewNote}
            onChange={(e) => setReviewNote(e.target.value)}
            size="small"
          />
          <FormControlLabel
            control={<Checkbox checked={actionNeeded} onChange={(e) => setActionNeeded(e.target.checked)} />}
            label="Action Needed"
          />
          <FormControlLabel
            control={<Checkbox checked={isFollowUp} onChange={(e) => setIsFollowUp(e.target.checked)} />}
            label="Follow-up"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          Submit Review
        </Button>
      </DialogActions>
    </Dialog>
  );
};
